import { z } from 'zod';

/**
 * Referral Earnings Schemas
 * Balance tracking, earnings, withdrawals, and in-app balance conversion
 */

// Withdrawal Method Enum
export const WithdrawalMethodSchema = z.enum(['STRIPE', 'WALLET', 'IN_APP_BALANCE'], {
  error: "Invalid withdrawal method"
});

// Withdrawal Status Enum
export const WithdrawalStatusSchema = z.enum(['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'CANCELED'], {
  error: "Invalid withdrawal status"
});

// --- Core Entities ---

// Earnings balance
export const EarningsBalanceSchema = z.object({
  totalEarnings: z.number(),
  availableBalance: z.number(),
  inAppBalance: z.number(),
  withdrawnTotal: z.number(),
});

// Individual referral earning
export const ReferralEarningSchema = z.object({
  id: z.string(),
  referrerId: z.string(),
  inviteeId: z.string(),
  jobId: z.string().optional(),
  amount: z.number(),
  commissionRate: z.number(),
  creditsBasis: z.number(),
  source: z.string(),
  createdAt: z.coerce.date(),
});

// Earnings withdrawal
export const EarningsWithdrawalSchema = z.object({
  id: z.string(),
  amount: z.number(),
  method: WithdrawalMethodSchema,
  status: WithdrawalStatusSchema,
  bonusAmount: z.number().optional(),
  processedAt: z.coerce.date().optional(),
  createdAt: z.coerce.date(),
});

// --- API Request Schemas ---

// Request a withdrawal
export const RequestWithdrawalSchema = z.object({
  amount: z.number().positive(),
  method: WithdrawalMethodSchema,
  walletAddress: z.string().optional(), // required if method is WALLET
});

// Convert earnings to in-app balance
export const ConvertToInAppBalanceSchema = z.object({
  amount: z.number().positive(),
});

// --- API Response Schemas ---

// Earnings summary response
export const EarningsSummarySchema = z.object({
  balance: EarningsBalanceSchema,
  recentEarnings: z.array(ReferralEarningSchema),
  recentWithdrawals: z.array(EarningsWithdrawalSchema),
});

// Export TypeScript types
export type WithdrawalMethod = z.infer<typeof WithdrawalMethodSchema>;
export type WithdrawalStatus = z.infer<typeof WithdrawalStatusSchema>;
export type EarningsBalance = z.infer<typeof EarningsBalanceSchema>;
export type ReferralEarning = z.infer<typeof ReferralEarningSchema>;
export type EarningsWithdrawal = z.infer<typeof EarningsWithdrawalSchema>;
export type RequestWithdrawal = z.infer<typeof RequestWithdrawalSchema>;
export type ConvertToInAppBalance = z.infer<typeof ConvertToInAppBalanceSchema>;
export type EarningsSummary = z.infer<typeof EarningsSummarySchema>;
